import { setImpersonatedMspId } from '../api/apiClient';
import { API_BASE_URL } from '../config/apiBase';

const API_BASE = API_BASE_URL;
const ACCESS_TOKEN_KEY = 'sc_access_token';
const REFRESH_TOKEN_KEY = 'sc_refresh_token';
const TOKEN_EXPIRY_KEY = 'sc_token_expiry';

export const authService = {
  getSsoStartUrl({ domain, returnUrl } = {}) {
    const params = new URLSearchParams();
    if (domain) params.set('domain', domain);
    params.set('returnUrl', returnUrl || `${window.location.origin}/auth/sso/callback`);
    return `${API_BASE}/auth/sso/start?${params.toString()}`;
  },

  async completeSso(code) {
    const resp = await fetch(`${API_BASE}/auth/sso/complete?code=${encodeURIComponent(code)}`, {
      headers: { 'Cache-Control': 'no-store' },
    });
    if (!resp.ok) throw new Error('SSO sign-in expired. Start sign-in again.');
    const data = await resp.json();
    this.storeTokens(data.accessToken, data.refreshToken, data.expiresIn);
    return data;
  },

  async login(email, password) {
    const resp = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.error || 'Login failed');
    }
    const data = await resp.json();
    if (!data.mfaRequired) {
      this.storeTokens(data.accessToken, data.refreshToken, data.expiresIn);
    }
    return data;
  },

  async verifyMfa(email, code) {
    const resp = await fetch(`${API_BASE}/auth/verify-mfa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    if (!resp.ok) throw new Error('Invalid MFA code');
    const data = await resp.json();
    this.storeTokens(data.accessToken, data.refreshToken, data.expiresIn);
    return data;
  },

  async refresh() {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) throw new Error('No refresh token');
    const resp = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!resp.ok) {
      // Only clear all tokens on auth rejection (401/403).
      // On server errors (5xx), preserve refresh token so retry is possible.
      if (resp.status === 401 || resp.status === 403) {
        this.clearTokens();
      }
      throw new Error(resp.status >= 500 ? 'Server unavailable' : 'Session expired');
    }
    const data = await resp.json();
    this.storeTokens(data.accessToken, data.refreshToken, data.expiresIn);
    return data.accessToken;
  },

  async logout() {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (refreshToken) {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAccessToken()}`,
        },
        body: JSON.stringify({ refreshToken }),
      }).catch(() => {});
    }
    this.clearTokens();
    setImpersonatedMspId(null);
    try { localStorage.removeItem('msp-branding-cache'); } catch (_) {}
    try { sessionStorage.removeItem('sentinel_theme'); } catch (_) {}
  },

  storeTokens(accessToken, refreshToken, expiresIn) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    // Default to 15 minutes (900s) if expiresIn not provided — matches backend config
    const expiry = Date.now() + ((expiresIn || 900) * 1000);
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiry.toString());
  },

  clearTokens() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  },

  getAccessToken() {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },

  isAuthenticated() {
    return !!localStorage.getItem(ACCESS_TOKEN_KEY);
  },

  hasRefreshToken() {
    return !!localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  isTokenExpiringSoon() {
    const expiry = parseInt(localStorage.getItem(TOKEN_EXPIRY_KEY) || '0', 10);
    return expiry - Date.now() < 5 * 60 * 1000;
  },
};
